import {
  Button,
  Conditional,
  ConditionalWrapper,
  Modal,
} from '@payaca/ambrosia';
import { FC, useCallback, useMemo } from 'react';

import { ErrorMessage } from '@payaca/components/feedbackMessage/FeedbackMessage';

import TextareaFieldMentionFormatter from '@payaca/components/textareaField/TextareaFieldMentionFormatter';
import ValidatedFieldWrapper from '@payaca/components/validatedFieldWrapper/ValidatedFieldWrapper';
import ValidatedForm from '@payaca/components/validatedForm/ValidatedForm';
import { getIsRequiredFieldValidator } from '@payaca/helpers/fieldValidationHelper';

import { FieldValidationResult } from '@payaca/types/fieldValidationTypes';
import { Note } from '@payaca/types/notesTypes';
import { User } from '@payaca/types/userTypes';

import {
  convertMarkdownToHtml,
  isValidHtml,
} from '@payaca/utilities/htmlUtilities';

import './CreateEditNoteControl.sass';

export type GraphqlSafeNote = Partial<
  Omit<Note, 'id'> & { id?: string | number }
>;

type Props = {
  note?: GraphqlSafeNote;
  error: string | null;
  isProcessing: boolean;
  onCreateUpdateNote: (note: GraphqlSafeNote) => void;
  usersForMentioning: Pick<User, 'id' | 'name'>[];
  withinModal?: boolean;
  onDeleteNote?: (notePublicId: string) => void;
};
const CreateEditNoteControlBase: FC<Props> = ({
  note = {},
  error,
  isProcessing,
  onCreateUpdateNote,
  usersForMentioning,
  withinModal = false,
  onDeleteNote,
}) => {
  const onSubmit = useCallback(
    (form: any) => {
      onCreateUpdateNote({
        ...note,
        description: form.description,
        dealId: form.dealId,
      });
    },
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- FIXME: pre-existing error: React Hook useCallback has a missing dependency: 'onCreateUpdateNote'
    [note]
  );

  const initialFormState = useMemo(() => {
    // convert old markdown notes to html if editing
    let description = '';
    if (note?.description) {
      description = isValidHtml(note.description)
        ? note.description
        : convertMarkdownToHtml(note.description);
    }
    return {
      dealId: note.dealId,
      description: description,
    };
  }, [note]);

  const fieldValidators = useMemo(() => {
    return {
      description: [getIsRequiredFieldValidator()],
    };
  }, []);

  const renderFormContents = useCallback(
    (
      isValid: boolean,
      formState: {
        [key: string]: any;
      },
      validationState: {
        [key: string]: FieldValidationResult;
      },
      touchedState: {
        [key: string]: boolean;
      },
      onFieldChange: (value: { [key: string]: any }) => void,
      onFieldTouch: (fieldName: string) => void
    ) => {
      return (
        <>
          <ConditionalWrapper condition={withinModal} Wrapper={Modal.Body}>
            <ValidatedFieldWrapper
              validationResult={validationState['description']}
              isTouched={touchedState['description'] || false}
            >
              <TextareaFieldMentionFormatter
                name="description"
                onChange={onFieldChange}
                value={formState.description}
                mentions={usersForMentioning.map((user) => ({
                  id: user.id,
                  value: user.name,
                }))}
              />
            </ValidatedFieldWrapper>
          </ConditionalWrapper>
          <ConditionalWrapper condition={withinModal} Wrapper={Modal.Footer}>
            {error && <ErrorMessage message={error} />}
            <ConditionalWrapper
              condition={withinModal}
              Wrapper={Modal.Footer.Actions}
            >
              <Conditional condition={!!onDeleteNote && !!note.publicId}>
                <Button
                  onClick={() => onDeleteNote?.(note.publicId!)}
                  colour="negative"
                  variant="secondary"
                >
                  Delete
                </Button>
              </Conditional>
              <Button
                onClick={() => onSubmit(formState)}
                disabled={!isValid}
                isProcessing={isProcessing}
              >
                {note?.id ? 'Save' : 'Create note'}
              </Button>
            </ConditionalWrapper>
          </ConditionalWrapper>
        </>
      );
    },
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- FIXME: pre-existing error: React Hook useCallback has a missing dependency: 'usersForMentioning'
    [error, isProcessing, note, onSubmit, withinModal, onDeleteNote]
  );
  return (
    <ValidatedForm
      renderFormContents={renderFormContents}
      fieldValidators={fieldValidators}
      initialFormState={initialFormState}
    />
  );
};

export default CreateEditNoteControlBase;
